<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ScheduleMessagePopover from './ScheduleMessagePopover.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const store = useStore();
const { t } = useI18n();

const open = ref(false);
const rescheduling = ref(null);
const confirmingCancel = ref(null);

const items = computed(() =>
  store.getters['scheduledMessages/getByConversation'](props.conversationId)
);
const uiFlags = computed(() => store.getters['scheduledMessages/getUIFlags']);

watch(
  () => props.conversationId,
  id => {
    open.value = false;
    rescheduling.value = null;
    confirmingCancel.value = null;
    if (id) store.dispatch('scheduledMessages/fetch', { conversationId: id });
  },
  { immediate: true }
);

const heading = item => {
  const when = format(
    new Date(item.scheduled_at),
    "EEE, d 'de' MMM 'às' HH:mm",
    {
      locale: ptBR,
    }
  );
  return item.user?.name ? `${when} · ${item.user.name}` : when;
};

const cancelItem = async item => {
  try {
    await store.dispatch('scheduledMessages/remove', {
      conversationId: props.conversationId,
      id: item.id,
    });
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGES.CANCELLED'));
  } catch (e) {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGES.UPDATE_ERROR'));
  } finally {
    confirmingCancel.value = null;
  }
};

const reschedule = async date => {
  try {
    await store.dispatch('scheduledMessages/update', {
      conversationId: props.conversationId,
      id: rescheduling.value.id,
      payload: { scheduled_at: date.toISOString() },
    });
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGES.RESCHEDULED'));
    rescheduling.value = null;
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.join(', ') ||
        t('CONVERSATION.SCHEDULED_MESSAGES.UPDATE_ERROR')
    );
  }
};
</script>

<template>
  <div
    v-show="items.length"
    class="mx-4 mb-1 rounded-lg border bg-n-amber-2 border-n-amber-6 text-n-amber-12"
  >
    <div class="flex justify-between items-center px-3 py-1.5 text-xs">
      <span class="flex gap-1.5 items-center">
        <span class="i-ph-clock size-3.5" />
        {{ t('CONVERSATION.SCHEDULED_MESSAGES.BAR_COUNT', items.length) }}
      </span>
      <NextButton
        ghost
        xs
        :label="
          open
            ? t('CONVERSATION.SCHEDULED_MESSAGES.CLOSE')
            : t('CONVERSATION.SCHEDULED_MESSAGES.BAR_VIEW')
        "
        @click="open = !open"
      />
    </div>
    <ul v-if="open" class="flex flex-col gap-1 px-3 pb-2">
      <li
        v-for="item in items"
        :key="item.id"
        class="flex gap-2 justify-between items-start p-2 text-xs rounded-md bg-n-solid-1 text-n-slate-12"
      >
        <div class="min-w-0">
          <p class="font-medium">{{ heading(item) }}</p>
          <p class="truncate text-n-slate-11">{{ item.content }}</p>
        </div>
        <div class="flex gap-1 shrink-0">
          <NextButton
            faded
            slate
            xs
            icon="i-ph-clock"
            :label="t('CONVERSATION.SCHEDULED_MESSAGES.RESCHEDULE')"
            @click="rescheduling = item"
          />
          <NextButton
            v-if="confirmingCancel !== item.id"
            faded
            ruby
            xs
            icon="i-ph-x"
            :label="t('CONVERSATION.SCHEDULED_MESSAGES.CANCEL_ITEM')"
            @click="confirmingCancel = item.id"
          />
          <NextButton
            v-else
            solid
            ruby
            xs
            :label="t('CONVERSATION.SCHEDULED_MESSAGES.CANCEL_CONFIRM_YES')"
            :is-loading="uiFlags.isDeleting"
            @click="cancelItem(item)"
          />
        </div>
      </li>
    </ul>
    <div v-if="rescheduling" class="relative">
      <div class="absolute bottom-0 left-3 z-40">
        <ScheduleMessagePopover
          mode="reschedule"
          :initial-value="new Date(rescheduling.scheduled_at)"
          :is-loading="uiFlags.isUpdating"
          @confirm="reschedule"
          @close="rescheduling = null"
        />
      </div>
    </div>
  </div>
</template>
